import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPScrollablePane, IPSearchBox, IPText } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import BaseTableList, { IBaseTableList } from 'components/TableList/BaseTableList';
import { globalFilterDelay } from 'globalConstants';
import {
  globalSearchBoxStyles,
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapSmall,
} from 'globalStyles';
import StringValue, { StringValueTypes } from 'models/stringValue/stringValue';
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IStringValueListProps {
  stringValues: StringValue[];
  existingStringValues: number[];
  isLoading: boolean;
  onSelectStringValues?: (stringValues: StringValue[]) => void;
  onAddNewStringValue?: () => void;
  type: StringValueTypes;
  canCreateNew: boolean;
  singleSelect?: boolean;
  hideSearch?: boolean;
  disableExistingItemView?: boolean;
}

export interface IStringValueList {
  scroll: (index: number, select?: boolean) => void;
}

const StringValueList = forwardRef(function (props: IStringValueListProps, ref) {
  const { t } = useTranslation(['translation', 'stringValue']);
  const appContext = useContext(AppContext);
  const [stringValuesDisplay, setStringValuesDisplay] = useState<StringValue[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const listRef = useRef<IBaseTableList<StringValue> | null>(null);

  useImperativeHandle(
    ref,
    (): IStringValueList => {
      return {
        scroll(index, select) {
          listRef.current?.scroll(index, select);
        },
      };
    },
    [],
  );

  //
  // Effects
  //
  useEffect(() => {
    setStringValuesDisplay(props.stringValues);

    //clean-up
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setStringValuesDisplay(props.stringValues);
    } else {
      const queryLower = searchQuery.toLowerCase();
      const filtered = props.stringValues.filter((entry) => {
        return Object.values(entry).some((val) => {
          if (typeof val === 'string') {
            if (entry.value && entry.value.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.value && entry.value.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.description && entry.description.toLowerCase().includes(queryLower)) {
              return true;
            }
          }

          return false;
        });
      });

      if (filtered) {
        setStringValuesDisplay(filtered);
      }
    }
  }, [props.stringValues, searchQuery]);

  //
  // Columns list view
  //
  const getColumnsList = (): IColumn[] => {
    const allColumns = [
      {
        key: 'name',
        name: t('stringValue:Column.Value'),
        minWidth: 150,
        maxWidth: 400,
        isRowHeader: true,
        isResizable: true,
        isMultiline: true,
      },
      {
        key: 'description',
        name: t('stringValue:Column.Description'),
        minWidth: 150,
        maxWidth: 400,
        isResizable: true,
        isMultiline: true,
      },
    ];

    return allColumns;
  };

  const [columns, setColumns] = useState<IColumn[]>(getColumnsList());

  const onRenderColumn = (
    item: StringValue | undefined,
    column: IColumn,
    index?: number | undefined,
  ): JSX.Element | null => {
    if (!item || !column) return null;
    switch (column.key) {
      case 'name':
        return <IPText>{item.value}</IPText>;
      case 'description':
        return <IPText>{item.description}</IPText>;
      default:
        return null;
    }
  };

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (filterText) {
      setSearchQuery(filterText.toLowerCase());
    } else {
      setSearchQuery('');
    }
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      <IPStack.Item>
        <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
          {!props.hideSearch && (
            <IPStack.Item>
              <IPSearchBox
                autoFocus={!appContext.isMobileView}
                underlined
                placeholder={t('translation:General.Filter.Placeholder')}
                id="search"
                onChange={(ev, newValue) => {
                  if (searchTimer) {
                    clearTimeout(searchTimer);
                  }
                  setSearchTimer(setTimeout(() => onChangeText(ev, newValue), globalFilterDelay));
                }}
                styles={globalSearchBoxStyles}
              />
            </IPStack.Item>
          )}
          {props.canCreateNew && (
            <IPStack.Item>
              <IPLink
                onClick={() => {
                  if (props.onAddNewStringValue) props.onAddNewStringValue();
                }}
              >
                {t('stringValue:AddStringValue.Link', { type: StringValue.getTypeText(props.type, true, t) })}
              </IPLink>
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack.Item>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <BaseTableList
            ref={listRef}
            compact
            items={stringValuesDisplay}
            columns={columns}
            selectionMode={props.singleSelect === true ? IPSelectionMode.single : IPSelectionMode.multiple}
            setColumns={setColumns}
            onRenderCol={onRenderColumn}
            disableSort={true}
            onSelect={props.onSelectStringValues}
            onCanSelect={(item) => {
              return !props.existingStringValues?.includes(item.stringValueId);
            }}
          />
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
});

export default StringValueList;
